import { useState, useCallback } from 'react';
import { analyticsApi } from '../services/analytics-api.service';
import type { EngagementMetricsQueryParams } from '../types/engagement-metrics.types';

/** Saves a CSV blob as `<prefix>-YYYY-MM-DD.csv`. */
export const triggerDownload = (blob: Blob, prefix = 'bquest-engagement-metrics-export') => {
  const timestamp = new Date().toISOString().split('T')[0];
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${prefix}-${timestamp}.csv`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking immediately can cancel the download in some browsers (notably Safari).
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * CSV export for the Engagements Log (ticket 16). Reuses the log's own query params
 * (cursor/limit ignored server-side), gated behind the `PIIExportDialog` consent flow at
 * the call site since the export contains initiator name/email.
 */
export const useEngagementMetricsExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportCSV = useCallback(async (params: Omit<EngagementMetricsQueryParams, 'cursor' | 'limit'>) => {
    setIsExporting(true);
    setExportError(null);
    try {
      const blob = await analyticsApi.exportEngagementMetricsCSV(params);
      triggerDownload(blob);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportCSV, isExporting, exportError };
};
